import { motion } from "framer-motion";
import { Play, ArrowDownRight } from "lucide-react";
import { CHURCH } from "@/lib/data";
import { useState, useEffect } from "react";

const SLIDES = [
  { src: "/slideshow/slide1.jpg", position: "top center" },   // retrato — rostos no topo
  { src: "/slideshow/slide2.jpg", position: "center center" }, // paisagem — ok
  { src: "/slideshow/slide3.jpg", position: "top center" },   // retrato — pastor no topo
  { src: "/slideshow/slide4.jpg", position: "top center" },   // retrato — pastor no topo
  { src: "/slideshow/slide5.jpg", position: "center center" }, // paisagem — ok
  { src: "/slideshow/slide6.jpg", position: "top center" },   // retrato — pastor no topo
];

export default function Hero() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % SLIDES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden flex items-end"
    >
      {/* Slideshow backgrounds — crossfade via opacity transition */}
      {SLIDES.map(({ src, position }, i) => (
        <div
          key={src}
          className="absolute inset-0 bg-cover scale-105 transition-opacity duration-1000"
          style={{
            backgroundImage: `url(${src})`,
            backgroundPosition: position,
            opacity: i === current ? 1 : 0,
          }}
          aria-hidden="true"
        />
      ))}

      {/* Gradient overlays for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/55 to-[#050505]" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />

      {/* Slide indicators */}
      <div className="absolute bottom-6 right-6 sm:right-10 z-20 flex gap-2">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            aria-label={`Slide ${i + 1}`}
            className={`h-[3px] rounded-full transition-all duration-500 ${
              i === current ? "w-8 bg-[#D4AF37]" : "w-4 bg-white/30"
            }`}
          />
        ))}
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pb-20 sm:pb-28 pt-32">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="text-lg sm:text-xl lg:text-2xl tracking-overline text-[#D4AF37] mb-6 font-semibold"
          data-testid="hero-eyebrow"
        >
          Bem-vindo à Primeira Igreja Brasileira de Greenville · Desde {CHURCH.founded}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="font-serif-display text-white text-5xl sm:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tight max-w-4xl"
          data-testid="hero-title"
        >
          Encontrando propósito,
          <br />
          <span className="italic text-white/90">vivendo em </span>
          <span className="text-[#D4AF37]">graça</span>
          <span className="italic text-white/90">.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.9 }}
          className="mt-8 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed"
          data-testid="hero-description"
        >
          Uma comunidade brasileira em Greenville, SC — proclamando o Evangelho
          de Jesus Cristo, fortalecendo famílias e formando discípulos para o
          Reino de Deus.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contato"
            data-testid="hero-cta-visit"
            className="inline-flex items-center gap-3 bg-[#D4AF37] text-black font-medium px-8 py-4 hover:bg-[#B3932E] transition-all duration-300 group"
          >
            Visite-nos
            <ArrowDownRight
              size={18}
              strokeWidth={1.8}
              className="group-hover:translate-x-1 group-hover:translate-y-1 transition-transform"
            />
          </a>
          <a
            href={CHURCH.socials.youtube.live}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-watch"
            className="inline-flex items-center gap-3 border border-white/20 text-white px-8 py-4 hover:bg-white/5 hover:border-white/40 transition-all duration-300"
          >
            <Play size={16} strokeWidth={1.8} className="fill-white" />
            Assista Online
          </a>
        </motion.div>

        {/* Bottom strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 1 }}
          className="mt-16 sm:mt-24 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl border-t border-white/10 pt-8"
          data-testid="hero-strip"
        >
          {[
            { k: "Domingo", v: "Família 18h30" },
            { k: "Quinta", v: "Discipulado · Oração" },
            { k: "Sábado", v: "Jovens 18h" },
            { k: "Mauldin · SC", v: "406 E Butler Rd" },
          ].map((it) => (
            <div key={it.k}>
              <p className="text-[10px] tracking-overline uppercase text-[#D4AF37] mb-1">
                {it.k}
              </p>
              <p className="text-sm text-white/85">{it.v}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
